// Finalized EzyDine plans — single source of truth for the home Pricing
// section and the /pricing comparison page.
// Benchmarked to undercut Petpooja (Core ₹10k / Growth ₹20k / Scale ₹40k).

export type Plan = {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  cta: string;
  popular: boolean;
  accent: string;
};

export const plans: Plan[] = [
  {
    name: "Starter",
    price: "₹6,999",
    period: "/year",
    description: "Everything to run the counter for a single small outlet going digital.",
    features: [
      "Cloud billing POS (Windows / Android / iOS)",
      "KOT printing (ESC/POS, station routing)",
      "Kitchen Display System (KDS)",
      "Menu & category management",
      "Unlimited users & terminals, staff PIN",
      "Core sales reports + dashboard",
      "On-chat support",
    ],
    cta: "Start 7-Day Free Trial",
    popular: false,
    accent: "border-background-200",
  },
  {
    name: "Growth",
    price: "₹8,999",
    period: "/year",
    description: "Everything in Starter, plus the full growth engine for busy restaurants.",
    features: [
      "Everything in Starter",
      "Scan & QR table ordering + digital menu",
      "Website + online ordering widget",
      "Captain / waiter app",
      "In-built CRM + loyalty (points)",
      "Inventory & stock management",
      "Feedback app (QR + link)",
      "All reports + custom report builder",
      "Table reservation + token management",
      "Hotel / room-service ordering (StayOS link)",
      "On-call + on-chat support",
    ],
    cta: "Start 7-Day Free Trial",
    popular: true,
    accent: "border-accent-500",
  },
  {
    name: "Pro",
    price: "₹11,999",
    period: "/year",
    description: "Everything in Growth, plus AI and the premium stack — still ₹28k under Petpooja Scale.",
    features: [
      "Everything in Growth",
      "Zomato / Swiggy integration + reconciliation",
      "🎙️ AI Voice Ordering (multilingual call-to-kitchen)",
      "Loyalty+ (tiers, virtual wallet / store credit)",
      "Tally / accounting export",
      "Multi-outlet management + consolidated reports",
      "Priority 24×7 support",
    ],
    cta: "Start 7-Day Free Trial",
    popular: false,
    accent: "border-primary-500",
  },
];

// Plan comparison matrix for the /pricing page.
// value per tier: true = included, "text" = qualified, false = not included.
export type Cell = boolean | string;

// Names here MUST mirror the per-plan `features` strings above so the home
// Pricing cards and the /pricing comparison table read identically.
export const comparison: { feature: string; tiers: [Cell, Cell, Cell] }[] = [
  // In every plan (Starter onward)
  { feature: "Cloud billing POS (Windows / Android / iOS)", tiers: [true, true, true] },
  { feature: "KOT printing (ESC/POS, station routing)", tiers: [true, true, true] },
  { feature: "Kitchen Display System (KDS)", tiers: [true, true, true] },
  { feature: "Menu & category management", tiers: [true, true, true] },
  { feature: "Unlimited users & terminals, staff PIN", tiers: [true, true, true] },
  { feature: "Core sales reports + dashboard", tiers: [true, true, true] },
  // Growth onward
  { feature: "Scan & QR table ordering + digital menu", tiers: [false, true, true] },
  { feature: "Website + online ordering widget", tiers: [false, true, true] },
  { feature: "Captain / waiter app", tiers: [false, true, true] },
  { feature: "In-built CRM + loyalty (points)", tiers: [false, true, true] },
  { feature: "Inventory & stock management", tiers: [false, true, true] },
  { feature: "Feedback app (QR + link)", tiers: [false, true, true] },
  { feature: "All reports + custom report builder", tiers: [false, true, true] },
  { feature: "Table reservation + token management", tiers: [false, true, true] },
  { feature: "Hotel / room-service ordering (StayOS link)", tiers: [false, true, true] },
  // Pro only
  { feature: "Zomato / Swiggy integration + reconciliation", tiers: [false, false, true] },
  { feature: "🎙️ AI Voice Ordering (multilingual call-to-kitchen)", tiers: [false, false, true] },
  { feature: "Loyalty+ (tiers, virtual wallet / store credit)", tiers: [false, false, true] },
  { feature: "Tally / accounting export", tiers: [false, false, true] },
  { feature: "Multi-outlet management + consolidated reports", tiers: [false, false, true] },
  // Tier-varying
  { feature: "Support", tiers: ["On-chat support", "On-call + on-chat support", "Priority 24×7 support"] },
];

export const planNames = ["Starter", "Growth", "Pro"] as const;
export const planPrices = ["₹6,999", "₹8,999", "₹11,999"] as const;
